import React, { useState, useContext, useEffect, useRef } from 'react';
import { SocketContext } from '../SocketProvider';
// import { deepCopy } from '../../CommentCenter/utils';
import { REQ_TYPE, deepCopy } from '../utils';
import { useClientType } from './useClientType';

export const NotificationCtx = React.createContext();

export const useNotification = () => {
  const context = useContext(NotificationCtx);

  if (context === undefined) {
    throw new Error(
      'useNotification is used outside of the NotificationProvider',
    );
  }
  return context;
};

export const DEFAULT_PAGE_SIZE = 20;

const NotificationProvider = ({
  projectId,
  email,
  name,
  isClient,
  children,
  ...restProps
}) => {
  const { socket, connected } = useContext(SocketContext);

  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [isNoMore, setIsNoMore] = useState(false);
  const [isReadCollapsed, setIsReadCollapsed] = useState(true);
  const [threadData, setThreadData] = useState([]);
  const { internalClients, externalClients } = useClientType(
    projectId,
    restProps.getProjectMember,
  );
  const store = useRef([]);

  const handleData = (data, type) => {
    if (type === REQ_TYPE.FETCH_LIST) {
      if (!data || !data.length) {
        setIsNoMore(true);
        return;
      }
      setThreadData(data);
      store.current = data;
      return;
    }

    if (type === REQ_TYPE.EDIT_NOTIFICATION_BY_SELF) {
      const newData = deepCopy(store.current || []).map((originItem) => {
        const editResItem = data.find((v) => v.id === originItem.id);
        if (editResItem) {
          return {
            ...originItem,
            ...editResItem,
            markAllStage: editResItem.readStatus ? true : false,
          };
        }
        return originItem;
      });
      setThreadData(newData);
      store.current = newData;
      return;
    }

    if (type === REQ_TYPE.LOAD_MORE) {
      if (!data || !data.length) {
        setIsNoMore(true);
        return;
      }
      setThreadData([...store.current, ...data]);
      store.current = [...store.current, ...data];
    }

    if (type === REQ_TYPE.ADD && data) {
      setThreadData([data, ...store.current]);
      store.current = [data, ...store.current];
    }
  };

  const lisDataRequest = (isLoadMore = false) => {
    if (isNoMore) {
      setLoadMoreLoading(false);
      setLoading(false);
      return;
    }
    let notificationId = 0;
    if (isLoadMore) {
      if (!store.current || !store.current.length) {
        return;
      }
      notificationId = store.current[store.current.length - 1].id;
      setLoadMoreLoading(true);
    } else {
      setLoading(true);
    }
    socket.emit(
      'notification:list',
      { pageSize: DEFAULT_PAGE_SIZE, email, notificationId, projectId },
      (data) => {
        // console.log(':::notification data::: ', JSON.stringify(data, null, 2));
        handleData(data, isLoadMore ? REQ_TYPE.LOAD_MORE : REQ_TYPE.FETCH_LIST);
        isLoadMore ? setLoadMoreLoading(false) : setLoading(false);
      },
    );
  };

  const markAllAsRead = () => {
    if (!store.current || !store.current.length) {
      return;
    }
    const unreadNotificationIds = store.current
      .filter((v) => !v.readStatus)
      .map((v) => v.id);

    socket.emit(
      'notification:edit',
      {
        notificationIds: unreadNotificationIds,
        email,
        projectId,
      },
      (data) => {
        handleData(data, REQ_TYPE.EDIT_NOTIFICATION_BY_SELF);
        setLoading(false);
      },
    );
  };

  const markAsRead = (id) => {
    socket.emit(
      'notification:edit',
      {
        notificationIds: [id],
        email,
        projectId,
      },
      (data) => {
        handleData(data, REQ_TYPE.EDIT_NOTIFICATION_BY_SELF);
        setLoading(false);
      },
    );
  };

  const loadMore = () => {
    if (!store.current || !store.current.length) {
      return;
    }
    setLoadMoreLoading(true);
    lisDataRequest(true);
  };

  const reorderUnreadList = () => {
    const newData = (store.current || []).map((msg) => {
      const { markAllStage, ...rest } = msg || {};
      return rest;
    });
    setThreadData(newData);
    store.current = newData;
  };

  useEffect(() => {
    setThreadData([]);
    setIsNoMore(false);
    if (connected && projectId) {
      lisDataRequest();
    }
    store.current = [];
  }, [connected, projectId]);

  useEffect(() => {
    if (email) {
      socket.on(`notification:add:${email}`, (data) => {
        setLoading(false);
        handleData(data, REQ_TYPE.ADD);
      });
      socket.on(`notification:refresh:${email}`, (data) => {
        // refresh commentDeleteFlag
        handleData(
          Array.isArray(data) ? data : [data],
          REQ_TYPE.EDIT_NOTIFICATION_BY_SELF,
        );
      });
    }
  }, [email]);

  return (
    <NotificationCtx.Provider
      value={{
        loading,
        loadMoreLoading,
        internalClients,
        externalClients,
        threadData,
        setThreadData,
        isReadCollapsed,
        selfName: name,
        selfEmail: email,
        isClient,
        setIsReadCollapsed,
        markAllAsRead,
        markAsRead,
        reorderUnreadList,
        loadMore,
        projectId,
        ...restProps,
      }}
    >
      {children}
    </NotificationCtx.Provider>
  );
};

export default NotificationProvider;
